import { createModel } from '@rematch/core';
import { Paged, SortParam } from 'domains/appDomain';
import {
  buildCreateEqptParam,
  buildUpdateEqptParam,
  CreateEqptParam,
  EqptFilterObj,
  EqptType,
  Equipment,
  ListEqptParam,
  PartialEqpt,
  UpdateEqptParam,
} from 'domains/eqptDomain';
import type { RootModel } from 'models';
import platformEqptService from 'services/eqptService';
import { FILTER_NOT_EXISTS } from 'utils/constants';
import { baseReducers, BaseState } from './baseReducers';

type EqptBaseSelectState = {
  visible: boolean;
  data?: Paged<Equipment>;
  currentPage: number;
  filterObj: EqptFilterObj;
  sort?: SortParam;
};

interface EqptState extends BaseState {
  data?: Paged<Equipment>;
  item?: PartialEqpt;
  eqptTypes: EqptType[];
  currentPage: number;
  pageSize: number;
  eqptBaseSelect: EqptBaseSelectState;
  noMarkupCount: number;
  filterObj: EqptFilterObj;
}

export const eqpt = createModel<RootModel>()({
  state: {
    data: undefined,
    item: undefined,
    eqptTypes: [],
    currentPage: 1,
    pageSize: 20,
    eqptBaseSelect: {
      visible: false,
      data: undefined,
      currentPage: 1,
      filterObj: {},
      sort: undefined,
    },
    noMarkupCount: 0,
    filterObj: {},
  } as EqptState,
  reducers: {
    ...baseReducers<EqptState>(),
    setEqptBaseSelectVisible: (
      state: EqptState,
      eqptBaseSelectVisible: boolean
    ) => {
      state.eqptBaseSelect.visible = eqptBaseSelectVisible;
      return state;
    },
    setEqptBaseSelectData: (state: EqptState, data: Paged<Equipment>) => {
      state.eqptBaseSelect.data = data;
      return state;
    },
    setEqptBaseSelectCurrentPage: (state: EqptState, currentPage: number) => {
      state.eqptBaseSelect.currentPage = currentPage;
      return state;
    },
    setEqptBaseSelectState: (
      state: EqptState,
      newState: Partial<EqptBaseSelectState>
    ) => {
      state.eqptBaseSelect = { ...state.eqptBaseSelect, ...newState };
      return state;
    },
    resetEqptBaseSelect: (state: EqptState) => {
      state.eqptBaseSelect = {
        ...state.eqptBaseSelect,
        visible: false,
        currentPage: 1,
        filterObj: {},
        sort: undefined,
      };
      return state;
    },
    reset: (state: EqptState) => {
      state.filterObj = {};
      state.item = undefined;
      return state;
    },
  },
  effects: (dispatch) => {
    const { eqpt } = dispatch;
    return {
      async getList(payload: ListEqptParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await platformEqptService.queryEqpts(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          eqpt.setState({
            data: resp,
            currentPage,
          });
        } else {
          eqpt.setState({
            data: resp,
          });
        }
      },

      async getListForBaseSelect(payload: ListEqptParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await platformEqptService.queryBaseEqpts(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          eqpt.setEqptBaseSelectData(resp);
          eqpt.setEqptBaseSelectCurrentPage(currentPage);
        } else {
          eqpt.setEqptBaseSelectData(resp);
        }
      },

      async saveItem(payload: PartialEqpt, rootState): Promise<void> {
        if (payload.id) {
          // update existing
          const updateParam: UpdateEqptParam = buildUpdateEqptParam(payload);
          await platformEqptService.updateEqpt(updateParam);
        } else {
          // create new
          const createParam: CreateEqptParam = buildCreateEqptParam(payload);
          await platformEqptService.createBaseEqpt(createParam);
        }
        await eqpt.getList({
          page: {
            currentPage: rootState.eqpt.currentPage,
            pageSize: rootState.eqpt.pageSize,
          },
          query: {
            ...rootState.eqpt.filterObj,
          },
        });
        await eqpt.getNoMarkupCount();
      },

      async createOrgFromBase(payload: number, rootState): Promise<void> {
        await platformEqptService.createOrgEqpt(payload);
        await eqpt.getList({
          page: {
            currentPage: rootState.eqpt.currentPage,
            pageSize: rootState.eqpt.pageSize,
          },
          query: {
            ...rootState.eqpt.filterObj,
          },
        });
        await eqpt.getNoMarkupCount();
      },

      async removeItem(payload: number, rootState): Promise<void> {
        await platformEqptService.removeEqpt(payload);
        await eqpt.getList({
          page: {
            currentPage: rootState.eqpt.currentPage,
            pageSize: rootState.eqpt.pageSize,
          },
          query: {
            ...rootState.eqpt.filterObj,
          },
        });
        await eqpt.getNoMarkupCount();
      },

      async getEqptTypes(): Promise<void> {
        const resp = await platformEqptService.getEqptTypes();
        eqpt.setState({
          eqptTypes: resp,
        });
      },

      async getNoMarkupCount(): Promise<void> {
        const query: ListEqptParam = {
          page: {
            pageSize: 1,
            currentPage: 1,
          },
          query: {
            active: true,
            markupPct: FILTER_NOT_EXISTS,
          },
        };
        const resp = await platformEqptService.queryEqpts(query);

        eqpt.setState({
          noMarkupCount: resp.totalElements,
        });
      },

      async saveBaseSelectFilter(payload: EqptFilterObj): Promise<void> {
        eqpt.setEqptBaseSelectState({
          currentPage: 1,
          filterObj: payload,
        });
      },

      async removeBaseSelectFilter(): Promise<void> {
        eqpt.setEqptBaseSelectState({
          currentPage: 1,
          filterObj: {},
        });
      },

      async saveFilter(payload: EqptFilterObj): Promise<void> {
        eqpt.setState({
          currentPage: 1,
          filterObj: payload,
        });
      },

      async removeFilter(): Promise<void> {
        eqpt.clearFilter();
      },
    };
  },
});
